import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Star, Zap, Shield, Mail, Phone, MapPin, Send, CheckCircle, Loader2, Quote, ChevronDown, ChevronUp, Check, Play, Menu, X, Facebook, Twitter, Instagram, Linkedin, Youtube } from 'lucide-react';
import api from '../utils/api';
import BlueskyIcon from '../components/icons/BlueskyIcon';
import LanguageSelector, { getPreferredLanguage } from '../components/LanguageSelector';
import PopupPageModal from '../components/PopupPageModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icon mapping
const iconMap = {
  star: Star,
  zap: Zap,
  shield: Shield,
  mail: Mail,
  phone: Phone,
  location: MapPin,
  check: Check,
};

const PublicTenantLandingPage = ({ tenantData: propTenantData, subdomain: propSubdomain, isSubdomainMode = false }) => {
  const { tenantId } = useParams();
  const [landingPage, setLandingPage] = useState(null);
  const [navigationMenu, setNavigationMenu] = useState(null);
  const [loading, setLoading] = useState(!isSubdomainMode);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Popup page modal state (for pages marked isPopup=true)
  const [popupPage, setPopupPage] = useState({ isOpen: false, pageSlug: null, previewToken: null });
  // List of all published pages (passed from API) so we can detect popup targets
  const pagesList = propTenantData?.pages || [];
  
  // Returns a click handler that opens a popup modal if `url` points to a popup
  // page, otherwise returns null (so default navigation happens).
  const getPopupClickHandler = (url) => {
    if (!url || url === '#' || url.startsWith('download:') || url.startsWith('http://') || url.startsWith('https://') || url.startsWith('#') || url.startsWith('mailto:') || url.startsWith('tel:')) {
      return null;
    }
    let needle = url.startsWith('/') ? url.substring(1) : url;
    needle = needle.split('?')[0].split('#')[0];
    if (!needle || !pagesList.length) return null;
    const page = pagesList.find(p =>
      p.slug === needle ||
      p.full_path === needle ||
      p.page_id === needle ||
      p.preview_token === needle
    );
    if (page && page.isPopup) {
      return (e) => {
        e.preventDefault();
        e.stopPropagation();
        setPopupPage({ isOpen: true, pageSlug: page.slug, previewToken: page.preview_token });
      };
    }
    return null;
  };

  // Normalize a stored link value into a correct href so external links work.
  // Handles: full URLs (as-is), mailto/tel/anchors (as-is), bare domains like
  // "example.com" (-> https://), download: links, and internal page slugs (-> /slug).
  const resolveHref = (url) => {
    if (!url) return '#';
    const u = String(url).trim();
    if (u.startsWith('download:')) return u.replace(/^download:/, '');
    if (/^(https?:\/\/|mailto:|tel:|#|\/)/i.test(u)) return u;
    if (/^[a-z0-9-]+(\.[a-z0-9-]+)+(\/.*)?$/i.test(u)) return `https://${u}`;
    return `/${u}`;
  };
  
  // Translation state
  const [currentLanguage, setCurrentLanguage] = useState(null);
  const [translatedNavLabels, setTranslatedNavLabels] = useState({});
  const [translatedSectionTexts, setTranslatedSectionTexts] = useState({});
  
  // Translation config from tenant data (fields are inside branding object from API)
  const branding = propTenantData?.branding || {};
  const translationEnabled = branding.translation_enabled || propTenantData?.translation_enabled;
  const defaultLanguage = branding.default_language || propTenantData?.translation_default_language || 'en';
  const enabledLanguages = branding.enabled_languages || propTenantData?.translation_languages || [];
  const tenant_id = propTenantData?.tenant_id;
  
  // Initialize language from stored preference
  useEffect(() => {
    if (translationEnabled && enabledLanguages.length > 0) {
      const savedLang = getPreferredLanguage();
      // Use saved language if it's in the enabled list, otherwise use default
      const lang = enabledLanguages.includes(savedLang) ? savedLang : defaultLanguage;
      setCurrentLanguage(lang);
    }
  }, [translationEnabled, enabledLanguages, defaultLanguage]);
  
  // Handle language change
  const handleLanguageChange = (lang) => {
    setCurrentLanguage(lang);
  };
  
  // Translate navigation labels when language changes
  useEffect(() => {
    if (!currentLanguage || currentLanguage === defaultLanguage || !navigationMenu?.items?.length) {
      setTranslatedNavLabels({});
      return;
    }
    
    const texts = navigationMenu.items.map(item => item.label).filter(Boolean);
    if (texts.length === 0) return;
    
    const translateLabels = async () => {
      try {
        const response = await fetch(`${API_URL}/api/translation/translate-texts`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            texts,
            target_language: currentLanguage,
            tenant_id: tenant_id,
            source_language: defaultLanguage
          })
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.translated_texts) {
            const labels = {};
            navigationMenu.items.forEach((item, idx) => {
              labels[item.item_id] = data.translated_texts[idx] || item.label;
            });
            setTranslatedNavLabels(labels);
          }
        }
      } catch (err) {
        console.error('[LandingPage] Nav translation error:', err);
      }
    };
    
    translateLabels();
  }, [currentLanguage, defaultLanguage, navigationMenu, tenant_id]);
  
  // Translate section texts (hero, features, testimonials, FAQ, footer)
  useEffect(() => {
    if (!currentLanguage || currentLanguage === defaultLanguage || !landingPage) {
      setTranslatedSectionTexts({});
      return;
    }
    
    const collectTexts = () => {
      const texts = {};
      const hero = landingPage.hero || {};
      const features = landingPage.features || {};
      const testimonials = landingPage.testimonials || {};
      const faq = landingPage.faq || {};
      const footer = landingPage.footer || {};
      const about = landingPage.about || {};
      
      if (hero.headline) texts['hero_headline'] = hero.headline;
      if (hero.subheadline) texts['hero_subheadline'] = hero.subheadline;
      if (hero.cta_text) texts['hero_cta'] = hero.cta_text;
      
      if (about.title) texts['about_title'] = about.title;
      if (about.content) texts['about_content'] = about.content;
      
      if (features.title) texts['features_title'] = features.title;
      if (features.items) {
        features.items.forEach((item, idx) => {
          if (item.title) texts[`feature_${idx}_title`] = item.title;
          if (item.description) texts[`feature_${idx}_desc`] = item.description;
        });
      }
      
      if (testimonials.title) texts['testimonials_title'] = testimonials.title;
      if (testimonials.items) {
        testimonials.items.forEach((item, idx) => {
          if (item.quote) texts[`testimonial_${idx}_quote`] = item.quote;
        });
      }
      
      if (faq.title) texts['faq_title'] = faq.title;
      if (faq.items) {
        faq.items.forEach((item, idx) => {
          if (item.question) texts[`faq_${idx}_question`] = item.question;
          if (item.answer) texts[`faq_${idx}_answer`] = item.answer;
        });
      }
      
      if (footer.text) texts['footer_text'] = footer.text;
      
      return texts;
    };
    
    const translateSections = async () => {
      const textsMap = collectTexts();
      const keys = Object.keys(textsMap);
      const values = Object.values(textsMap);
      
      if (values.length === 0) return;
      
      try {
        const response = await fetch(`${API_URL}/api/translation/translate-texts`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            texts: values,
            target_language: currentLanguage,
            tenant_id: tenant_id,
            source_language: defaultLanguage
          })
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.translated_texts) {
            const result = {};
            keys.forEach((key, idx) => {
              result[key] = data.translated_texts[idx] || textsMap[key];
            });
            setTranslatedSectionTexts(result);
          }
        }
      } catch (err) {
        console.error('[LandingPage] Section translation error:', err);
      }
    };
    
    translateSections();
  }, [currentLanguage, defaultLanguage, landingPage, tenant_id]);
  
  // Helper to get translated text
  const t = (key, fallback) => translatedSectionTexts[key] || fallback;
  const getNavLabel = (item) => translatedNavLabels[item.item_id] || item.label;
  
  // Refs for click-outside detection
  const navRef = useRef(null);
  const mobileMenuRef = useRef(null);
  
  // Close mobile menu when clicking outside
  const handleClickOutside = useCallback((event) => {
    // Check if click is outside the nav area
    if (navRef.current && !navRef.current.contains(event.target)) {
      setMobileMenuOpen(false);
    }
  }, []);
  
  // Add/remove click outside listener
  useEffect(() => {
    if (mobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [mobileMenuOpen, handleClickOutside]);

  // Control DynatexSaaS badge visibility based on branding settings
  // Badge is hidden by default (index.html). We only reveal it once branding allows it.
  useEffect(() => {
    const branding = propTenantData?.branding;
    if (branding && branding.show_platform_branding !== false) {
      document.body.classList.add('show-dynatex-badge');
    } else {
      document.body.classList.remove('show-dynatex-badge');
    }

    // Cleanup on unmount
    return () => {
      document.body.classList.remove('show-dynatex-badge');
    };
  }, [propTenantData]);

  // Handle subdomain mode - use prop data directly
  useEffect(() => {
    if (isSubdomainMode && propTenantData) {
      setLandingPage(propTenantData.landing_page);
      setLoading(false);
      
      // Fetch navigation menu if configured
      if (propTenantData.landing_page?.navigation?.enabled && propTenantData.landing_page?.navigation?.menu_id) {
        api.get(`/public/navigation/${propTenantData.landing_page.navigation.menu_id}`)
          .then(menuRes => setNavigationMenu(menuRes.data))
          .catch(err => console.error('Failed to fetch navigation menu:', err));
      }
    }
  }, [isSubdomainMode, propTenantData]);

  // Apply tenant favicon directly — avoids duplicate <link rel="icon"> from Helmet + static index.html
  useEffect(() => {
    const favUrl = landingPage?.branding?.favicon_url;
    if (!favUrl) return;
    ['icon', 'shortcut icon', 'apple-touch-icon'].forEach(rel => {
      let link = document.querySelector(`link[rel="${rel}"]`);
      if (!link) { link = document.createElement('link'); link.rel = rel; document.head.appendChild(link); }
      link.href = favUrl;
    });
    return () => {
      ['icon', 'shortcut icon', 'apple-touch-icon'].forEach(rel => {
        const link = document.querySelector(`link[rel="${rel}"]`);
        if (link) link.href = `${process.env.PUBLIC_URL || ''}/favicon.png`;
      });
    };
  }, [landingPage?.branding?.favicon_url]);

  useEffect(() => {
    const fetchLandingPage = async () => {
      // Skip fetch if in subdomain mode
      if (isSubdomainMode) return;

      try {
        const res = await api.get(`/public/site/${tenantId}`);
        setLandingPage(res.data);
        
        // Fetch navigation menu if configured
        if (res.data.navigation?.enabled && res.data.navigation?.menu_id) {
          try {
            const menuRes = await api.get(`/public/navigation/${res.data.navigation.menu_id}`);
            setNavigationMenu(menuRes.data);
          } catch (menuErr) {
            console.error('Failed to fetch navigation menu:', menuErr);
          }
        }
      } catch (err) {
        setError(err.response?.data?.detail || 'Landing page not found');
      } finally {
        setLoading(false);
      }
    };

    if (tenantId && !isSubdomainMode) {
      fetchLandingPage();
    }
  }, [tenantId, isSubdomainMode]);

  // Get link URL for navigation items
  const getNavLink = (item) => {
    if (item.link_type === 'external') return item.link_value || '#';
    if (item.link_type === 'anchor') return `#${item.link_value?.replace(/^#/, '') || ''}`;
    return '#';
  };

  const renderNavItem = (item, onClick = undefined) => {
    if (item.is_button) {
      const btnBg = item.button_bg_color || '#4f46e5';
      const btnText = item.button_text_color || '#ffffff';
      const btnStyle = item.button_style || 'solid';
      const btnRadius = item.button_border_radius === 'pill' ? '9999px' : item.button_border_radius === 'square' ? '2px' : '6px';
      let btnInlineStyle;
      if (btnStyle === 'outline') {
        btnInlineStyle = { background: 'transparent', color: btnBg, border: `1.5px solid ${btnBg}`, borderRadius: btnRadius };
      } else if (btnStyle === 'ghost') {
        btnInlineStyle = { background: `${btnBg}18`, color: btnBg, borderRadius: btnRadius };
      } else {
        btnInlineStyle = { backgroundColor: btnBg, color: btnText, borderRadius: btnRadius };
      }
      return (
        <a
          key={item.item_id}
          href={getNavLink(item)}
          target={item.target || '_self'}
          onClick={onClick}
          className="inline-flex items-center px-4 py-1.5 font-semibold transition-all hover:opacity-80"
          style={btnInlineStyle}
        >
          {getNavLabel(item)}
        </a>
      );
    }
    return (
      <a
        key={item.item_id}
        href={getNavLink(item)}
        target={item.target || '_self'}
        onClick={onClick}
        className="hover:opacity-80 transition-opacity font-medium"
      >
        {getNavLabel(item)}
      </a>
    );
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    await new Promise(resolve => setTimeout(resolve, 1000));
    setFormSubmitted(true);
    setSubmitting(false);
  };

  const getVideoEmbedUrl = (url) => {
    if (!url) return null;
    // YouTube
    const ytMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]+)/);
    if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;
    // Vimeo
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
    return url;
  };

  // Block renderers
  const renderBlock = (block, theme) => {
    if (!block.enabled) return null;

    switch (block.type) {
      case 'text':
        return (
          <section key={block.id} className="py-16 px-6">
            <div className={`max-w-4xl mx-auto text-${block.alignment || 'center'}`}>
              {block.title && (
                <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{ color: theme?.primary_color }}>
                  {block.title}
                </h2>
              )}
              <div className="text-lg leading-relaxed whitespace-pre-line opacity-80">
                {block.content}
              </div>
            </div>
          </section>
        );

      case 'image':
        return (
          <section key={block.id} className="py-16 px-6">
            <div className="max-w-4xl mx-auto">
              {block.url && (
                <figure>
                  <img 
                    src={block.url} 
                    alt={block.alt || ''} 
                    className={`rounded-lg shadow-lg mx-auto ${block.width === 'full' ? 'w-full' : 'max-w-2xl'}`}
                  />
                  {block.caption && (
                    <figcaption className="text-center mt-4 text-sm opacity-60">{block.caption}</figcaption>
                  )}
                </figure>
              )}
            </div>
          </section>
        );

      case 'gallery':
        return (
          <section key={block.id} className="py-16 px-6" style={{ backgroundColor: theme?.background_color === '#ffffff' ? '#f8fafc' : 'rgba(255,255,255,0.05)' }}>
            <div className="max-w-6xl mx-auto">
              {block.title && (
                <h2 className="text-3xl md:text-4xl font-bold mb-10 text-center" style={{ color: theme?.primary_color }}>
                  {block.title}
                </h2>
              )}
              <div className={`grid grid-cols-2 md:grid-cols-${block.columns || 3} gap-4`}>
                {block.images?.filter(img => img.url).map((img) => (
                  <img 
                    key={img.id}
                    src={img.url} 
                    alt={img.alt || ''} 
                    className="rounded-lg shadow-md w-full h-48 object-cover hover:scale-105 transition-transform"
                  />
                ))}
              </div>
            </div>
          </section>
        );

      case 'testimonials':
        return (
          <section key={block.id} className="py-16 px-6">
            <div className="max-w-6xl mx-auto">
              {block.title && (
                <h2 className="text-3xl md:text-4xl font-bold mb-10 text-center" style={{ color: theme?.primary_color }}>
                  {block.title}
                </h2>
              )}
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {block.items?.map((item) => (
                  <div 
                    key={item.id}
                    className="p-6 rounded-xl shadow-md"
                    style={{ backgroundColor: theme?.background_color === '#ffffff' ? '#f8fafc' : 'rgba(255,255,255,0.1)' }}
                  >
                    <Quote size={32} className="mb-4 opacity-20" style={{ color: theme?.primary_color }} />
                    <p className="text-lg mb-4 italic">&ldquo;{item.quote}&rdquo;</p>
                    <div className="flex items-center gap-3">
                      {item.avatar && (
                        <img src={item.avatar} alt={item.author} className="w-12 h-12 rounded-full object-cover" />
                      )}
                      <div>
                        <p className="font-semibold">{item.author}</p>
                        {item.role && <p className="text-sm opacity-60">{item.role}</p>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        );

      case 'faq':
        return (
          <section key={block.id} className="py-16 px-6" style={{ backgroundColor: theme?.background_color === '#ffffff' ? '#f8fafc' : 'rgba(255,255,255,0.05)' }}>
            <div className="max-w-3xl mx-auto">
              {block.title && (
                <h2 className="text-3xl md:text-4xl font-bold mb-10 text-center" style={{ color: theme?.primary_color }}>
                  {block.title}
                </h2>
              )}
              <div className="space-y-4">
                {block.items?.map((item) => (
                  <div 
                    key={item.id}
                    className="border rounded-lg overflow-hidden"
                    style={{ backgroundColor: theme?.background_color || '#ffffff' }}
                  >
                    <button
                      className="w-full px-6 py-4 flex items-center justify-between text-left font-medium"
                      onClick={() => setExpandedFaq(expandedFaq === item.id ? null : item.id)}
                    >
                      <span>{item.question}</span>
                      {expandedFaq === item.id ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                    </button>
                    {expandedFaq === item.id && (
                      <div className="px-6 pb-4 opacity-80">
                        {item.answer}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>
        );

      case 'pricing':
        return (
          <section key={block.id} className="py-16 px-6">
            <div className="max-w-6xl mx-auto">
              {block.title && (
                <h2 className="text-3xl md:text-4xl font-bold mb-10 text-center" style={{ color: theme?.primary_color }}>
                  {block.title}
                </h2>
              )}
              <div className={`grid md:grid-cols-${Math.min(block.items?.length || 1, 3)} gap-6`}>
                {block.items?.map((plan) => (
                  <div 
                    key={plan.id}
                    className={`p-6 rounded-xl shadow-md ${plan.highlighted ? 'ring-2 scale-105' : ''}`}
                    style={{ 
                      backgroundColor: plan.highlighted ? `${theme?.primary_color}10` : (theme?.background_color === '#ffffff' ? '#f8fafc' : 'rgba(255,255,255,0.1)'),
                      ringColor: theme?.primary_color
                    }}
                  >
                    {plan.highlighted && (
                      <div 
                        className="text-xs font-semibold text-white px-3 py-1 rounded-full mb-4 inline-block"
                        style={{ backgroundColor: theme?.primary_color }}
                      >
                        Most Popular
                      </div>
                    )}
                    <h3 className="text-xl font-bold mb-2">{plan.name}</h3>
                    <div className="mb-4">
                      <span className="text-4xl font-bold" style={{ color: theme?.primary_color }}>{plan.price}</span>
                      <span className="opacity-60">{plan.period}</span>
                    </div>
                    <ul className="space-y-2 mb-6">
                      {plan.features?.map((feature, idx) => (
                        <li key={idx} className="flex items-center gap-2">
                          <Check size={16} style={{ color: theme?.primary_color }} />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <a
                      href={plan.checkout_url ? plan.checkout_url : (plan.button_url ? resolveHref(plan.button_url) : '#')}
                      {...(plan.checkout_url ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                      onClick={plan.checkout_url ? undefined : (getPopupClickHandler(plan.button_url) || undefined)}
                      className="block w-full py-3 rounded-lg font-semibold text-white text-center transition-opacity hover:opacity-90"
                      style={{ backgroundColor: theme?.primary_color }}
                      data-testid={`tenant-pricing-cta-${plan.id || ''}`}
                    >
                      {plan.button_text || 'Get Started'}
                    </a>
                  </div>
                ))}
              </div>
            </div>
          </section>
        );

      case 'video':
        const embedUrl = getVideoEmbedUrl(block.url);
        return (
          <section key={block.id} className="py-16 px-6">
            <div className="max-w-4xl mx-auto">
              {block.title && (
                <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center" style={{ color: theme?.primary_color }}>
                  {block.title}
                </h2>
              )}
              {embedUrl && (
                <div className="aspect-video rounded-xl overflow-hidden shadow-lg">
                  <iframe
                    src={embedUrl}
                    title={block.title || 'Video'}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              )}
            </div>
          </section>
        );

      case 'cta':
        const ctaStyles = {
          primary: { backgroundColor: theme?.primary_color, color: '#ffffff' },
          secondary: { backgroundColor: theme?.secondary_color, color: '#ffffff' },
          dark: { backgroundColor: '#1e293b', color: '#ffffff' },
        };
        const style = ctaStyles[block.style] || ctaStyles.primary;
        const ctaPopup = getPopupClickHandler(block.button_url);
        return (
          <section 
            key={block.id} 
            className="py-20 px-6"
            style={style}
          >
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">{block.title}</h2>
              {block.subtitle && <p className="text-lg opacity-90 mb-8">{block.subtitle}</p>}
              {block.button_text && (
                <a
                  href={block.button_url ? resolveHref(block.button_url) : '#'}
                  onClick={ctaPopup || undefined}
                  className="inline-block px-8 py-4 rounded-lg font-semibold transition-all hover:scale-105"
                  style={{ 
                    backgroundColor: style.color,
                    color: style.backgroundColor
                  }}
                >
                  {block.button_text}
                </a>
              )}
            </div>
          </section>
        );

      case 'team':
        return (
          <section key={block.id} className="py-16 px-6" style={{ backgroundColor: theme?.background_color === '#ffffff' ? '#f8fafc' : 'rgba(255,255,255,0.05)' }}>
            <div className="max-w-6xl mx-auto">
              {block.title && (
                <h2 className="text-3xl md:text-4xl font-bold mb-10 text-center" style={{ color: theme?.primary_color }}>
                  {block.title}
                </h2>
              )}
              <div className={`grid md:grid-cols-2 lg:grid-cols-${Math.min(block.members?.length || 1, 4)} gap-6`}>
                {block.members?.map((member) => (
                  <div 
                    key={member.id}
                    className="text-center p-6 rounded-xl"
                    style={{ backgroundColor: theme?.background_color || '#ffffff' }}
                  >
                    {member.image ? (
                      <img 
                        src={member.image} 
                        alt={member.name} 
                        className="w-24 h-24 rounded-full mx-auto mb-4 object-cover shadow-md"
                      />
                    ) : (
                      <div 
                        className="w-24 h-24 rounded-full mx-auto mb-4 flex items-center justify-center text-2xl font-bold text-white"
                        style={{ backgroundColor: theme?.primary_color }}
                      >
                        {member.name?.charAt(0) || '?'}
                      </div>
                    )}
                    <h3 className="font-semibold text-lg">{member.name}</h3>
                    {member.role && <p className="opacity-60">{member.role}</p>}
                    {member.bio && <p className="mt-2 text-sm opacity-80">{member.bio}</p>}
                  </div>
                ))}
              </div>
            </div>
          </section>
        );

      case 'stats':
        return (
          <section 
            key={block.id} 
            className="py-16 px-6"
            style={{ backgroundColor: theme?.primary_color, color: '#ffffff' }}
          >
            <div className="max-w-6xl mx-auto">
              {block.title && (
                <h2 className="text-3xl md:text-4xl font-bold mb-10 text-center">{block.title}</h2>
              )}
              <div className={`grid grid-cols-2 md:grid-cols-${Math.min(block.items?.length || 1, 4)} gap-8`}>
                {block.items?.map((stat) => (
                  <div key={stat.id} className="text-center">
                    <div className="text-4xl md:text-5xl font-bold mb-2">{stat.value}</div>
                    <div className="opacity-80">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        );

      case 'html':
        return (
          <section key={block.id} className={block.full_width ? '' : 'px-6 py-16'}>
            {block.css && (
              <style dangerouslySetInnerHTML={{ __html: block.css }} />
            )}
            <div 
              className={block.full_width ? '' : 'max-w-6xl mx-auto'}
              dangerouslySetInnerHTML={{ __html: block.html || '' }} 
            />
          </section>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <Loader2 className="animate-spin mx-auto mb-4 text-indigo-600" size={48} />
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Helmet><title>Page Not Found</title></Helmet>
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Page Not Found</h1>
          <p className="text-slate-600">{error}</p>
        </div>
      </div>
    );
  }

  if (!landingPage) return null;

  const { hero, about, features, blocks, contact, footer, theme, tenant_name } = landingPage;

  // Build a single page title for the browser tab.
  // Priority: explicit landing title → hero headline (stripped) → tenant name → fallback.
  const stripTags = (s) => (typeof s === 'string' ? s.replace(/<[^>]*>/g, '').trim() : '');
  const pageTitle = (
    stripTags(landingPage.meta_title) ||
    tenant_name ||
    stripTags(hero?.headline) ||
    'Welcome'
  );
  const pageDescription = stripTags(landingPage.meta_description) || stripTags(hero?.subheadline) || '';

  // Shared <Helmet> for the public landing page — sets browser tab title and
  // a short meta description so the tab no longer reads "Loading...".
  const helmet = (
    <Helmet>
      <title>{pageTitle}</title>
      {pageDescription && <meta name="description" content={pageDescription.slice(0, 160)} />}
      {/* Favicon applied via useEffect above — not via Helmet, to avoid
          duplicate <link rel="icon"> tags causing browser to show NexusCMS default. */}
      {/* Mobile browser chrome tint using the tenant's brand colour. */}
      {theme?.primary_color && (
        <meta name="theme-color" content={theme.primary_color} />
      )}
      {/* Per-tenant PWA manifest — branded "Add to Home Screen" experience. */}
      {typeof window !== 'undefined' && (
        <link
          rel="manifest"
          href={`/api/public/manifest.webmanifest?domain=${encodeURIComponent(window.location.hostname)}`}
        />
      )}
    </Helmet>
  );

  // Full HTML Mode - Render custom HTML sections with Navigation & Footer
  if (landingPage.use_html_mode) {
    const sections = landingPage.html_sections || [];
    
    // Fallback to old single HTML field if no sections exist
    if (sections.length === 0 && landingPage.custom_html) {
      return (
        <>
          {helmet}
          {landingPage.custom_css && (
            <style dangerouslySetInnerHTML={{ __html: landingPage.custom_css }} />
          )}
          <div dangerouslySetInnerHTML={{ __html: landingPage.custom_html }} />
        </>
      );
    }

    // Filter out draft sections
    const liveSections = sections.filter(section => !section.draft);

    return (
      <div 
        className="min-h-screen"
        style={{ 
          backgroundColor: theme?.background_color || '#ffffff',
          color: theme?.text_color || '#1e293b'
        }}
      >
        {helmet}
        {/* Global CSS */}
        {landingPage.custom_css && (
          <style dangerouslySetInnerHTML={{ __html: landingPage.custom_css }} />
        )}

        {/* Language Selector for HTML Mode — position controlled by
            Site Theme (floating / top_bar / hidden). */}
        {translationEnabled && enabledLanguages.length > 0 && currentLanguage && (theme?.language_selector_position || 'floating') !== 'hidden' && (
          (theme?.language_selector_position || 'floating') === 'top_bar' ? (
            <div className="sticky top-0 z-[100] w-full bg-slate-50 border-b border-slate-200 flex justify-end items-center px-4 py-1.5">
              <LanguageSelector
                currentLanguage={currentLanguage}
                onLanguageChange={handleLanguageChange}
                enabledLanguages={enabledLanguages}
                variant="compact"
              />
            </div>
          ) : (
            <div className="fixed bottom-4 left-4 md:top-4 md:right-4 md:bottom-auto md:left-auto z-[100]">
              <LanguageSelector
                currentLanguage={currentLanguage}
                onLanguageChange={handleLanguageChange}
                enabledLanguages={enabledLanguages}
                variant="compact"
              />
            </div>
          )
        )}

        {/* Navigation for HTML Mode */}
        {landingPage.navigation?.enabled && navigationMenu && (
          <nav 
            ref={navRef}
            className={`w-full z-50 ${landingPage.navigation?.sticky ? 'sticky top-0' : ''} ${
              landingPage.navigation?.transparent ? 'bg-transparent' : ''
            }`}
            style={{ 
              backgroundColor: landingPage.navigation?.transparent ? 'transparent' : (landingPage.navigation?.background_color || theme?.primary_color || '#4F46E5'),
              color: landingPage.navigation?.text_color || '#ffffff'
            }}
          >
            <div className="max-w-6xl mx-auto px-6 py-4">
              <div className={`flex items-center ${navigationMenu.style === 'hamburger' && navigationMenu.hamburger_align === 'center' ? 'justify-between relative' : 'justify-between'}`}>
                {/* Logo - hidden when hamburger is left aligned */}
                <a 
                  href="/" 
                  aria-label="Go to homepage"
                  data-testid="landing-nav-logo-link-top"
                  className={`flex items-center gap-2 hover:opacity-80 transition-opacity ${navigationMenu.style === 'hamburger' && navigationMenu.hamburger_align === 'left' ? 'order-last' : ''}`}
                >
                  {landingPage.navigation?.logo_url ? (
                    <img 
                      src={landingPage.navigation.logo_url} 
                      alt="Logo" 
                      className="h-10 w-auto"
                    />
                  ) : (
                    <span className="text-xl font-bold">
                      {landingPage.navigation?.logo_text || tenant_name || 'Company'}
                    </span>
                  )}
                </a>

                {/* Render menu based on style */}
                {navigationMenu.style === 'hamburger' ? (
                  <>
                    {/* Hamburger Menu - Alignment based layout */}
                    {navigationMenu.hamburger_align === 'left' && (
                      <button 
                        className="p-2 hover:bg-white/10 rounded-lg transition-colors order-first"
                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                      >
                        {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
                      </button>
                    )}
                    {navigationMenu.hamburger_align === 'center' && (
                      <button 
                        className="p-2 hover:bg-white/10 rounded-lg transition-colors absolute left-1/2 transform -translate-x-1/2"
                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                      >
                        {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
                      </button>
                    )}
                    {(navigationMenu.hamburger_align === 'right' || !navigationMenu.hamburger_align) && (
                      <button 
                        className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                      >
                        {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
                      </button>
                    )}
                  </>
                ) : (
                  <>
                    {/* Horizontal Menu - Desktop */}
                    <div className="hidden md:flex items-center gap-8">
                      {navigationMenu.items?.map((item) => renderNavItem(item))}
                    </div>

                    {/* Mobile Menu Button - Only for horizontal on small screens */}
                    <button 
                      className="md:hidden p-2"
                      onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                    >
                      {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
                    </button>
                  </>
                )}
              </div>

              {/* Dropdown Menu for Hamburger or Mobile Horizontal */}
              {mobileMenuOpen && (
                <div className={`${navigationMenu.style === 'hamburger' ? '' : 'md:hidden'} mt-4 pb-4 border-t border-white/20 pt-4`}>
                  <div className="flex flex-col gap-4">
                    {navigationMenu.items?.map((item) => renderNavItem(item, () => setMobileMenuOpen(false)))}
                  </div>
                </div>
              )}
            </div>
          </nav>
        )}

        {/* Render each live HTML section (skip drafts) */}
        {liveSections.map((section) => {
          const anchorId = section.anchor_id || section.name?.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || section.id;
          return (
            <div key={section.id} id={anchorId}>
              {section.css && (
                <style dangerouslySetInnerHTML={{ __html: section.css }} />
              )}
              <div dangerouslySetInnerHTML={{ __html: section.html || '' }} />
            </div>
          );
        })}

        {/* Footer for HTML Mode */}
        {footer?.enabled !== false && (
          <footer 
            className="py-12 px-6"
            style={{ 
              backgroundColor: footer?.background_color || theme?.primary_color || '#4F46E5',
              color: footer?.text_color || '#ffffff'
            }}
          >
            <div className="max-w-6xl mx-auto">
              {/* Centered Style Footer */}
              {footer?.style === 'centered' && (
                <div className="text-center space-y-6">
                  {/* Logo */}
                  {footer?.logo_url ? (
                    <img src={footer.logo_url} alt="Logo" className="h-12 mx-auto" />
                  ) : footer?.company_name && (
                    <h3 className="text-2xl font-bold">{footer.company_name}</h3>
                  )}
                  
                  {footer?.tagline && (
                    <p className="opacity-80 max-w-md mx-auto">{footer.tagline}</p>
                  )}

                  {/* Social Links */}
                  {(footer?.social_links?.facebook || footer?.social_links?.twitter || footer?.social_links?.instagram || footer?.social_links?.linkedin || footer?.social_links?.youtube || footer?.social_links?.bluesky) && (
                    <div className="flex justify-center gap-4">
                      {footer?.social_links?.facebook && (
                        <a href={footer.social_links.facebook} target="_blank" rel="noopener noreferrer" className="hover:opacity-80">
                          <Facebook size={24} />
                        </a>
                      )}
                      {footer?.social_links?.twitter && (
                        <a href={footer.social_links.twitter} target="_blank" rel="noopener noreferrer" className="hover:opacity-80">
                          <Twitter size={24} />
                        </a>
                      )}
                      {footer?.social_links?.instagram && (
                        <a href={footer.social_links.instagram} target="_blank" rel="noopener noreferrer" className="hover:opacity-80">
                          <Instagram size={24} />
                        </a>
                      )}
                      {footer?.social_links?.linkedin && (
                        <a href={footer.social_links.linkedin} target="_blank" rel="noopener noreferrer" className="hover:opacity-80">
                          <Linkedin size={24} />
                        </a>
                      )}
                      {footer?.social_links?.youtube && (
                        <a href={footer.social_links.youtube} target="_blank" rel="noopener noreferrer" className="hover:opacity-80">
                          <Youtube size={24} />
                        </a>
                      )}
                      {footer?.social_links?.bluesky && (
                        <a href={footer.social_links.bluesky} target="_blank" rel="noopener noreferrer" className="hover:opacity-80">
                          <BlueskyIcon size={24} />
                        </a>
                      )}
                    </div>
                  )}

                  {/* Contact Info */}
                  {(footer?.contact_info?.email || footer?.contact_info?.phone || footer?.contact_info?.address) && (
                    <div className="flex flex-wrap justify-center gap-6 text-sm opacity-80">
                      {footer?.contact_info?.email && (
                        <a href={`mailto:${footer.contact_info.email}`} className="flex items-center gap-2 hover:opacity-100">
                          <Mail size={16} /> {footer.contact_info.email}
                        </a>
                      )}
                      {footer?.contact_info?.phone && (
                        <a href={`tel:${footer.contact_info.phone}`} className="flex items-center gap-2 hover:opacity-100">
                          <Phone size={16} /> {footer.contact_info.phone}
                        </a>
                      )}
                      {footer?.contact_info?.address && (
                        <span className="flex items-center gap-2">
                          <MapPin size={16} /> {footer.contact_info.address}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Simple Style Footer */}
              {(footer?.style === 'simple' || !footer?.style) && (
                <div className="text-center space-y-4">
                  {/* Social Links for Simple Footer */}
                  {(footer?.social_links?.facebook || footer?.social_links?.twitter || footer?.social_links?.instagram || footer?.social_links?.linkedin || footer?.social_links?.youtube) && (
                    <div className="flex justify-center gap-4 mb-4">
                      {footer?.social_links?.facebook && (
                        <a href={footer.social_links.facebook} target="_blank" rel="noopener noreferrer" className="hover:opacity-80">
                          <Facebook size={20} />
                        </a>
                      )}
                      {footer?.social_links?.twitter && (
                        <a href={footer.social_links.twitter} target="_blank" rel="noopener noreferrer" className="hover:opacity-80">
                          <Twitter size={20} />
                        </a>
                      )}
                      {footer?.social_links?.instagram && (
                        <a href={footer.social_links.instagram} target="_blank" rel="noopener noreferrer" className="hover:opacity-80">
                          <Instagram size={20} />
                        </a>
                      )}
                      {footer?.social_links?.linkedin && (
                        <a href={footer.social_links.linkedin} target="_blank" rel="noopener noreferrer" className="hover:opacity-80">
                          <Linkedin size={20} />
                        </a>
                      )}
                      {footer?.social_links?.youtube && (
                        <a href={footer.social_links.youtube} target="_blank" rel="noopener noreferrer" className="hover:opacity-80">
                          <Youtube size={20} />
                        </a>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Columns Style Footer */}
              {footer?.style === 'columns' && (
                <div className="grid md:grid-cols-4 gap-8 mb-8">
                  {/* Company Info */}
                  <div className="space-y-4">
                    {footer?.logo_url ? (
                      <img src={footer.logo_url} alt="Logo" className="h-10" />
                    ) : footer?.company_name && (
                      <h3 className="text-xl font-bold">{footer.company_name}</h3>
                    )}
                    {footer?.tagline && (
                      <p className="opacity-80 text-sm">{footer.tagline}</p>
                    )}
                    {/* Social Links */}
                    <div className="flex gap-3">
                      {footer?.social_links?.facebook && (
                        <a href={footer.social_links.facebook} target="_blank" rel="noopener noreferrer" className="hover:opacity-80">
                          <Facebook size={20} />
                        </a>
                      )}
                      {footer?.social_links?.twitter && (
                        <a href={footer.social_links.twitter} target="_blank" rel="noopener noreferrer" className="hover:opacity-80">
                          <Twitter size={20} />
                        </a>
                      )}
                      {footer?.social_links?.instagram && (
                        <a href={footer.social_links.instagram} target="_blank" rel="noopener noreferrer" className="hover:opacity-80">
                          <Instagram size={20} />
                        </a>
                      )}
                      {footer?.social_links?.linkedin && (
                        <a href={footer.social_links.linkedin} target="_blank" rel="noopener noreferrer" className="hover:opacity-80">
                          <Linkedin size={20} />
                        </a>
                      )}
                      {footer?.social_links?.youtube && (
                        <a href={footer.social_links.youtube} target="_blank" rel="noopener noreferrer" className="hover:opacity-80">
                          <Youtube size={20} />
                        </a>
                      )}
                    </div>
                  </div>

                  {/* Contact Info */}
                  <div className="space-y-4">
                    <h4 className="font-semibold">Contact</h4>
                    {footer?.contact_info?.email && (
                      <a href={`mailto:${footer.contact_info.email}`} className="flex items-center gap-2 text-sm opacity-80 hover:opacity-100">
                        <Mail size={16} /> {footer.contact_info.email}
                      </a>
                    )}
                    {footer?.contact_info?.phone && (
                      <a href={`tel:${footer.contact_info.phone}`} className="flex items-center gap-2 text-sm opacity-80 hover:opacity-100">
                        <Phone size={16} /> {footer.contact_info.phone}
                      </a>
                    )}
                    {footer?.contact_info?.address && (
                      <span className="flex items-center gap-2 text-sm opacity-80">
                        <MapPin size={16} /> {footer.contact_info.address}
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Copyright - Always shown */}
              <div className="text-center pt-6 border-t border-white/20">
                <p className="opacity-80 text-sm">
                  {footer?.copyright || `© ${new Date().getFullYear()} ${tenant_name || 'Company'}. All rights reserved.`}
                </p>
              </div>
            </div>
          </footer>
        )}
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen"
      style={{ 
        backgroundColor: theme?.background_color || '#ffffff',
        color: theme?.text_color || '#1e293b'
      }}
    >
      {helmet}
      {/* Navigation */}
      {landingPage.navigation?.enabled && navigationMenu && (
        <nav 
          ref={navRef}
          className={`w-full z-50 ${landingPage.navigation?.sticky ? 'sticky top-0' : ''} ${
            landingPage.navigation?.transparent ? 'bg-transparent' : ''
          }`}
          style={{ 
            backgroundColor: landingPage.navigation?.transparent ? 'transparent' : (hero?.background_color || theme?.primary_color || '#4F46E5'),
            color: hero?.text_color || '#ffffff'
          }}
        >
          <div className="max-w-6xl mx-auto px-6 py-4">
            <div className={`flex items-center ${navigationMenu.style === 'hamburger' && navigationMenu.hamburger_align === 'center' ? 'justify-between relative' : 'justify-between'}`}>
              {/* Logo */}
              <a 
                href="/" 
                aria-label="Go to homepage"
                data-testid="landing-nav-logo-link-bottom"
                className={`flex items-center gap-2 hover:opacity-80 transition-opacity ${navigationMenu.style === 'hamburger' && navigationMenu.hamburger_align === 'left' ? 'order-last' : ''}`}
              >
                {landingPage.navigation?.logo_url ? (
                  <img 
                    src={landingPage.navigation.logo_url} 
                    alt="Logo" 
                    className="h-10 w-auto"
                  />
                ) : (
                  <span className="text-xl font-bold">
                    {landingPage.navigation?.logo_text || tenant_name || 'Company'}
                  </span>
                )}
              </a>

              {/* Render menu based on style */}
              {navigationMenu.style === 'hamburger' ? (
                <>
                  {/* Hamburger Menu - Alignment based layout */}
                  {navigationMenu.hamburger_align === 'left' && (
                    <button 
                      className="p-2 hover:bg-white/10 rounded-lg transition-colors order-first"
                      onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                    >
                      {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
                    </button>
                  )}
                  {navigationMenu.hamburger_align === 'center' && (
                    <button 
                      className="p-2 hover:bg-white/10 rounded-lg transition-colors absolute left-1/2 transform -translate-x-1/2"
                      onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                    >
                      {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
                    </button>
                  )}
                  {(navigationMenu.hamburger_align === 'right' || !navigationMenu.hamburger_align) && (
                    <button 
                      className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                      onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                    >
                      {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
                    </button>
                  )}
                </>
              ) : (
                <>
                  {/* Horizontal Menu - Desktop */}
                  <div className="hidden md:flex items-center gap-8">
                    {navigationMenu.items?.map((item) => renderNavItem(item))}
                  </div>

                  {/* Mobile Menu Button - Only for horizontal on small screens */}
                  <button 
                    className="md:hidden p-2"
                    onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  >
                    {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
                  </button>
                </>
              )}
            </div>

            {/* Dropdown Menu for Hamburger or Mobile Horizontal */}
            {mobileMenuOpen && (
              <div className={`${navigationMenu.style === 'hamburger' ? '' : 'md:hidden'} mt-4 pb-4 border-t border-white/20 pt-4`}>
                <div className="flex flex-col gap-4">
                  {navigationMenu.items?.map((item) => renderNavItem(item, () => setMobileMenuOpen(false)))}
                </div>
              </div>
            )}
          </div>
        </nav>
      )}

      {/* Language Selector — position controlled by Site Theme
          (floating / top_bar / hidden). */}
      {translationEnabled && enabledLanguages.length > 0 && currentLanguage && (theme?.language_selector_position || 'floating') !== 'hidden' && (
        (theme?.language_selector_position || 'floating') === 'top_bar' ? (
          <div className="sticky top-0 z-[100] w-full bg-slate-50 border-b border-slate-200 flex justify-end items-center px-4 py-1.5">
            <LanguageSelector
              currentLanguage={currentLanguage}
              onLanguageChange={handleLanguageChange}
              enabledLanguages={enabledLanguages}
              variant="compact"
            />
          </div>
        ) : (
          <div className="fixed bottom-4 left-4 md:top-4 md:right-4 md:bottom-auto md:left-auto z-[100]">
            <LanguageSelector
              currentLanguage={currentLanguage}
              onLanguageChange={handleLanguageChange}
              enabledLanguages={enabledLanguages}
              variant="compact"
            />
          </div>
        )
      )}

      {/* Hero Section */}
      <section 
        id="hero"
        className={`min-h-[70vh] flex items-center px-6 py-20 ${hero?.image_url && hero?.image_position === 'background' ? 'relative' : ''}`}
        style={{ 
          backgroundColor: hero?.background_color || theme?.primary_color || '#4F46E5',
          color: hero?.text_color || '#ffffff',
          ...(hero?.image_url && hero?.image_position === 'background' ? {
            backgroundImage: `linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), url(${hero.image_url})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          } : {})
        }}
      >
        <div className={`max-w-6xl mx-auto w-full ${
          hero?.image_url && hero?.image_position !== 'background' 
            ? 'grid md:grid-cols-2 gap-12 items-center' 
            : ''
        }`}>
          {/* Text Content - Order changes based on image position */}
          <div className={`${
            hero?.image_url && hero?.image_position === 'left' ? 'order-2' : 'order-1'
          } ${!hero?.image_url || hero?.image_position === 'background' ? 'text-center max-w-4xl mx-auto' : ''}`}>
            <h1 
              className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              {t('hero_headline', hero?.headline || 'Welcome')}
            </h1>
            <p className="text-lg md:text-xl opacity-90 mb-8 whitespace-pre-line">
              {t('hero_subheadline', hero?.subheadline || '')}
            </p>
            {hero?.cta_text && (
              <a
                href={hero?.cta_url ? resolveHref(hero.cta_url) : '#contact'}
                onClick={getPopupClickHandler(hero?.cta_url) || undefined}
                className="inline-block px-8 py-4 rounded-lg text-lg font-semibold transition-all hover:opacity-90 hover:scale-105"
                style={{ 
                  backgroundColor: hero?.text_color || '#ffffff',
                  color: hero?.background_color || theme?.primary_color || '#4F46E5'
                }}
              >
                {t('hero_cta', hero.cta_text)}
              </a>
            )}
          </div>
          
          {/* Hero Image (if not background) */}
          {hero?.image_url && hero?.image_position !== 'background' && (
            <div className={`${hero?.image_position === 'left' ? 'order-1' : 'order-2'}`}>
              <img 
                src={hero.image_url} 
                alt="Hero" 
                className="w-full max-w-lg mx-auto rounded-lg shadow-2xl"
              />
            </div>
          )}
        </div>
      </section>

      {/* About Section */}
      {about?.enabled && (
        <section id="about" className="py-20 px-6">
          <div className="max-w-4xl mx-auto">
            <h2 
              className="text-3xl md:text-4xl font-bold mb-8 text-center"
              style={{ color: theme?.primary_color }}
            >
              {t('about_title', about?.title || 'About Us')}
            </h2>
            <div className={`grid ${about?.image_url ? 'md:grid-cols-2' : 'grid-cols-1'} gap-8 items-center`}>
              <div className="text-lg leading-relaxed whitespace-pre-line">
                {t('about_content', about?.content || '')}
              </div>
              {about?.image_url && (
                <img 
                  src={about.image_url} 
                  alt="About" 
                  className="rounded-lg shadow-lg w-full"
                />
              )}
            </div>
          </div>
        </section>
      )}

      {/* Features Section */}
      {features?.enabled && features?.items?.length > 0 && (
        <section 
          id="features" 
          className="py-20 px-6"
          style={{ backgroundColor: theme?.background_color === '#ffffff' ? '#f8fafc' : 'rgba(255,255,255,0.05)' }}
        >
          <div className="max-w-6xl mx-auto">
            <h2 
              className="text-3xl md:text-4xl font-bold mb-12 text-center"
              style={{ color: theme?.primary_color }}
            >
              {t('features_title', features?.title || 'What We Offer')}
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.items.map((item, index) => {
                const IconComponent = iconMap[item.icon] || Star;
                return (
                  <div 
                    key={item.id || index}
                    className="p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow"
                    style={{ backgroundColor: theme?.background_color || '#ffffff' }}
                  >
                    <div 
                      className="w-12 h-12 rounded-lg flex items-center justify-center mb-4"
                      style={{ backgroundColor: `${theme?.primary_color}20` }}
                    >
                      <IconComponent 
                        size={24} 
                        style={{ color: theme?.primary_color }} 
                      />
                    </div>
                    <h3 className="text-xl font-semibold mb-2">{t(`feature_${index}_title`, item.title)}</h3>
                    <p className="opacity-80">{t(`feature_${index}_desc`, item.description)}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Custom Blocks */}
      {blocks?.map((block) => renderBlock(block, theme))}

      {/* Contact Section */}
      {contact?.enabled && (
        <section id="contact" className="py-20 px-6">
          <div className="max-w-4xl mx-auto">
            <h2 
              className="text-3xl md:text-4xl font-bold mb-12 text-center"
              style={{ color: theme?.primary_color }}
            >
              {contact?.title || 'Get in Touch'}
            </h2>
            
            <div className={`grid ${contact?.show_form ? 'md:grid-cols-2' : 'grid-cols-1'} gap-12`}>
              {/* Contact Info */}
              <div className="space-y-6">
                {contact?.email && (
                  <div className="flex items-center gap-4">
                    <div 
                      className="w-12 h-12 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${theme?.primary_color}20` }}
                    >
                      <Mail size={24} style={{ color: theme?.primary_color }} />
                    </div>
                    <div>
                      <p className="text-sm opacity-60">Email</p>
                      <a 
                        href={`mailto:${contact.email}`}
                        className="font-medium hover:underline"
                        style={{ color: theme?.primary_color }}
                      >
                        {contact.email}
                      </a>
                    </div>
                  </div>
                )}
                
                {contact?.phone && (
                  <div className="flex items-center gap-4">
                    <div 
                      className="w-12 h-12 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${theme?.primary_color}20` }}
                    >
                      <Phone size={24} style={{ color: theme?.primary_color }} />
                    </div>
                    <div>
                      <p className="text-sm opacity-60">Phone</p>
                      <a 
                        href={`tel:${contact.phone}`}
                        className="font-medium hover:underline"
                        style={{ color: theme?.primary_color }}
                      >
                        {contact.phone}
                      </a>
                    </div>
                  </div>
                )}
                
                {contact?.address && (
                  <div className="flex items-center gap-4">
                    <div 
                      className="w-12 h-12 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${theme?.primary_color}20` }}
                    >
                      <MapPin size={24} style={{ color: theme?.primary_color }} />
                    </div>
                    <div>
                      <p className="text-sm opacity-60">Address</p>
                      <p className="font-medium whitespace-pre-line">{contact.address}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Contact Form */}
              {contact?.show_form && (
                <div 
                  className="p-6 rounded-xl shadow-md"
                  style={{ backgroundColor: theme?.background_color === '#ffffff' ? '#f8fafc' : 'rgba(255,255,255,0.05)' }}
                >
                  {formSubmitted ? (
                    <div className="text-center py-8">
                      <CheckCircle 
                        size={48} 
                        className="mx-auto mb-4" 
                        style={{ color: theme?.primary_color }} 
                      />
                      <h3 className="text-xl font-semibold mb-2">Thank You!</h3>
                      <p className="opacity-80">We&apos;ll get back to you soon.</p>
                    </div>
                  ) : (
                    <form onSubmit={handleFormSubmit} className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium mb-1">Name</label>
                        <input
                          type="text"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          required
                          className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2"
                          style={{ borderColor: '#e2e8f0' }}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Email</label>
                        <input
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          required
                          className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2"
                          style={{ borderColor: '#e2e8f0' }}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Message</label>
                        <textarea
                          value={formData.message}
                          onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                          required
                          rows={4}
                          className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2"
                          style={{ borderColor: '#e2e8f0' }}
                        />
                      </div>
                      <button
                        type="submit"
                        disabled={submitting}
                        className="w-full py-3 rounded-lg font-semibold text-white flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-50"
                        style={{ backgroundColor: theme?.primary_color }}
                      >
                        {submitting ? (
                          <Loader2 className="animate-spin" size={20} />
                        ) : (
                          <>
                            <Send size={20} />
                            Send Message
                          </>
                        )}
                      </button>
                    </form>
                  )}
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      {footer?.enabled !== false && (
        <footer 
          className="py-12 px-6"
          style={{ 
            backgroundColor: footer?.background_color || hero?.background_color || theme?.primary_color,
            color: footer?.text_color || hero?.text_color || '#ffffff'
          }}
        >
          <div className="max-w-6xl mx-auto">
            {/* Centered Style Footer */}
            {footer?.style === 'centered' && (
              <div className="text-center space-y-6">
                {/* Logo */}
                {footer?.logo_url ? (
                  <img src={footer.logo_url} alt="Logo" className="h-12 mx-auto" />
                ) : footer?.company_name && (
                  <h3 className="text-2xl font-bold">{footer.company_name}</h3>
                )}
                
                {footer?.tagline && (
                  <p className="opacity-80 max-w-md mx-auto">{footer.tagline}</p>
                )}

                {/* Social Links */}
                {(footer?.social_links?.facebook || footer?.social_links?.twitter || footer?.social_links?.instagram || footer?.social_links?.linkedin || footer?.social_links?.youtube) && (
                  <div className="flex justify-center gap-4">
                    {footer?.social_links?.facebook && (
                      <a href={footer.social_links.facebook} target="_blank" rel="noopener noreferrer" className="hover:opacity-80">
                        <Facebook size={24} />
                      </a>
                    )}
                    {footer?.social_links?.twitter && (
                      <a href={footer.social_links.twitter} target="_blank" rel="noopener noreferrer" className="hover:opacity-80">
                        <Twitter size={24} />
                      </a>
                    )}
                    {footer?.social_links?.instagram && (
                      <a href={footer.social_links.instagram} target="_blank" rel="noopener noreferrer" className="hover:opacity-80">
                        <Instagram size={24} />
                      </a>
                    )}
                    {footer?.social_links?.linkedin && (
                      <a href={footer.social_links.linkedin} target="_blank" rel="noopener noreferrer" className="hover:opacity-80">
                        <Linkedin size={24} />
                      </a>
                    )}
                    {footer?.social_links?.youtube && (
                      <a href={footer.social_links.youtube} target="_blank" rel="noopener noreferrer" className="hover:opacity-80">
                        <Youtube size={24} />
                      </a>
                    )}
                  </div>
                )}

                {/* Contact Info */}
                {(footer?.contact_info?.email || footer?.contact_info?.phone || footer?.contact_info?.address) && (
                  <div className="flex flex-wrap justify-center gap-6 text-sm opacity-80">
                    {footer?.contact_info?.email && (
                      <a href={`mailto:${footer.contact_info.email}`} className="flex items-center gap-2 hover:opacity-100">
                        <Mail size={16} /> {footer.contact_info.email}
                      </a>
                    )}
                    {footer?.contact_info?.phone && (
                      <a href={`tel:${footer.contact_info.phone}`} className="flex items-center gap-2 hover:opacity-100">
                        <Phone size={16} /> {footer.contact_info.phone}
                      </a>
                    )}
                    {footer?.contact_info?.address && (
                      <span className="flex items-center gap-2">
                        <MapPin size={16} /> {footer.contact_info.address}
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Simple Style Footer */}
            {(footer?.style === 'simple' || !footer?.style) && (
              <div className="text-center space-y-4">
                {/* Social Links for Simple Footer */}
                {(footer?.social_links?.facebook || footer?.social_links?.twitter || footer?.social_links?.instagram || footer?.social_links?.linkedin || footer?.social_links?.youtube) && (
                  <div className="flex justify-center gap-4 mb-4">
                    {footer?.social_links?.facebook && (
                      <a href={footer.social_links.facebook} target="_blank" rel="noopener noreferrer" className="hover:opacity-80">
                        <Facebook size={20} />
                      </a>
                    )}
                    {footer?.social_links?.twitter && (
                      <a href={footer.social_links.twitter} target="_blank" rel="noopener noreferrer" className="hover:opacity-80">
                        <Twitter size={20} />
                      </a>
                    )}
                    {footer?.social_links?.instagram && (
                      <a href={footer.social_links.instagram} target="_blank" rel="noopener noreferrer" className="hover:opacity-80">
                        <Instagram size={20} />
                      </a>
                    )}
                    {footer?.social_links?.linkedin && (
                      <a href={footer.social_links.linkedin} target="_blank" rel="noopener noreferrer" className="hover:opacity-80">
                        <Linkedin size={20} />
                      </a>
                    )}
                    {footer?.social_links?.youtube && (
                      <a href={footer.social_links.youtube} target="_blank" rel="noopener noreferrer" className="hover:opacity-80">
                        <Youtube size={20} />
                      </a>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Columns Style Footer */}
            {footer?.style === 'columns' && (
              <div className="grid md:grid-cols-4 gap-8 mb-8">
                {/* Company Info */}
                <div className="space-y-4">
                  {footer?.logo_url ? (
                    <img src={footer.logo_url} alt="Logo" className="h-10" />
                  ) : footer?.company_name && (
                    <h3 className="text-xl font-bold">{footer.company_name}</h3>
                  )}
                  {footer?.tagline && (
                    <p className="opacity-80 text-sm">{footer.tagline}</p>
                  )}
                  {/* Social Links */}
                  <div className="flex gap-3">
                    {footer?.social_links?.facebook && (
                      <a href={footer.social_links.facebook} target="_blank" rel="noopener noreferrer" className="hover:opacity-80">
                        <Facebook size={20} />
                      </a>
                    )}
                    {footer?.social_links?.twitter && (
                      <a href={footer.social_links.twitter} target="_blank" rel="noopener noreferrer" className="hover:opacity-80">
                        <Twitter size={20} />
                      </a>
                    )}
                    {footer?.social_links?.instagram && (
                      <a href={footer.social_links.instagram} target="_blank" rel="noopener noreferrer" className="hover:opacity-80">
                        <Instagram size={20} />
                      </a>
                    )}
                    {footer?.social_links?.linkedin && (
                      <a href={footer.social_links.linkedin} target="_blank" rel="noopener noreferrer" className="hover:opacity-80">
                        <Linkedin size={20} />
                      </a>
                    )}
                    {footer?.social_links?.youtube && (
                      <a href={footer.social_links.youtube} target="_blank" rel="noopener noreferrer" className="hover:opacity-80">
                        <Youtube size={20} />
                      </a>
                    )}
                  </div>
                </div>

                {/* Contact Info */}
                <div className="space-y-4">
                  <h4 className="font-semibold">Contact</h4>
                  {footer?.contact_info?.email && (
                    <a href={`mailto:${footer.contact_info.email}`} className="flex items-center gap-2 text-sm opacity-80 hover:opacity-100">
                      <Mail size={16} /> {footer.contact_info.email}
                    </a>
                  )}
                  {footer?.contact_info?.phone && (
                    <a href={`tel:${footer.contact_info.phone}`} className="flex items-center gap-2 text-sm opacity-80 hover:opacity-100">
                      <Phone size={16} /> {footer.contact_info.phone}
                    </a>
                  )}
                  {footer?.contact_info?.address && (
                    <span className="flex items-center gap-2 text-sm opacity-80">
                      <MapPin size={16} /> {footer.contact_info.address}
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Copyright - Always shown */}
            <div className="text-center pt-6 border-t border-white/20">
              <p className="opacity-80 text-sm">
                {footer?.copyright || `© ${new Date().getFullYear()} ${tenant_name || 'Company'}. All rights reserved.`}
              </p>
            </div>
          </div>
        </footer>
      )}

      {/* Popup Page Modal - renders pages marked isPopup over the current page */}
      <PopupPageModal
        pageSlug={popupPage.pageSlug}
        previewToken={popupPage.previewToken}
        isOpen={popupPage.isOpen}
        onClose={() => setPopupPage({ isOpen: false, pageSlug: null, previewToken: null })}
        siteTheme={theme}
        pages={pagesList}
        subdomain={propSubdomain}
        currentLanguage={currentLanguage}
        defaultLanguage={defaultLanguage}
        tenantId={tenant_id}
      />
    </div>
  );
};

export default PublicTenantLandingPage;
